import React from 'react';
import { LucideIcon } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: string;
    positive?: boolean;
  };
  icon: LucideIcon;
  badge?: string;
  glow?: boolean;
  onClick?: () => void;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  icon: Icon,
  badge,
  glow = false,
  onClick
}) => {
  return (
    <div 
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl p-6 transition-all duration-300 border text-left
        bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-[#f5f5f0]
        light:bg-white light:border-[#D5E4DC] light:text-[#0D1C17] light:shadow-sm
        ${onClick ? 'cursor-pointer hover:-translate-y-1 hover:shadow-glow-sm hover:border-brand-500/50 active:scale-[0.99]' : ''}
        ${glow ? 'shadow-glow-sm border-brand-500/40' : 'hover:border-brand-500/30'}
      `}
      title={onClick ? 'Click to view full analytic breakdown' : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Bioluminescent subtle background flare */}
      <div className="absolute -top-12 -right-12 w-28 h-28 bg-brand-500/5 rounded-full blur-2xl pointer-events-none" />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] truncate">
              {title}
            </p>
            {badge && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-500/15 text-brand-400 dark:text-brand-400 border border-brand-500/20 light:bg-emerald-100 light:text-emerald-700 light:border-emerald-200 shrink-0">
                {badge}
              </span>
            )}
          </div>
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white dark:text-white light:text-[#0D1C17] truncate">
            {value}
          </h3>
        </div>

        <div className="shrink-0 rounded-xl p-3 bg-[#22493c] dark:bg-[#22493c] text-brand-400 dark:text-brand-400 light:bg-emerald-50 light:text-emerald-600 shadow-sm">
          <Icon className="w-6 h-6" />
        </div>
      </div>

      {(subtitle || trend) && (
        <div className="mt-4 flex items-center justify-between gap-2 text-xs text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
          <div className="flex items-center gap-2 truncate">
            {trend && (
              <span className={`font-semibold flex items-center shrink-0 ${trend.positive ? 'text-emerald-400 dark:text-emerald-400 light:text-emerald-600' : 'text-rose-400 dark:text-rose-400 light:text-rose-600'}`}>
                {trend.positive ? '↑ ' : '↓ '}
                {trend.value}
              </span>
            )}
            {subtitle && <span className="truncate">{subtitle}</span>}
          </div>
          {onClick && (
            <span className="text-[10px] font-bold text-brand-400 dark:text-brand-400 light:text-emerald-700 shrink-0 hover:underline">
              Breakdown ↗
            </span>
          )}
        </div>
      )}
    </div>
  );
};
